import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { AutocompleteField } from 'components/form';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { CreditSubmissionDetailsDialogFormValues } from 'modules/creditSubmission/types';
import {
  getContactsAssignmentsForDetailsCard,
  getDetailsDialogDefaultFormValues,
  getUpsertCreditSubmissionAssignmentTitlesInput,
} from 'modules/creditSubmission/lib';
import { getUsersSelectOptions } from 'utils/selectOptionsHelper';
import { UPSERT_CREDIT_SUBMISSION_ASSIGNMENT_TITLES } from 'modules/creditSubmission/api';

export const DetailsDialog = () => {
  const {
    isOpen,
    handleClose,
    data: { contactsAssignments, setContactsAssignments, creditSubmissionId, contactsById },
  } = useModal(ModalsKeys.CreditSubmissionDetailsDialog);

  const { showNotification } = useNotifications();

  const { control, handleSubmit } = useForm<CreditSubmissionDetailsDialogFormValues>({
    defaultValues: getDetailsDialogDefaultFormValues(contactsAssignments, contactsById),
  });

  const { fields } = useFieldArray({
    control,
    name: 'contactsAssignments',
  });

  const [upsertCreditSubmissionAssignmentTitles, { loading: isLoading }] = useMutation(
    UPSERT_CREDIT_SUBMISSION_ASSIGNMENT_TITLES
  );

  const handleDetailsDialogSave = async (formData: CreditSubmissionDetailsDialogFormValues) => {
    try {
      const input = getUpsertCreditSubmissionAssignmentTitlesInput(contactsAssignments, formData);
      const result = await upsertCreditSubmissionAssignmentTitles({ variables: { input } });
      setContactsAssignments(
        getContactsAssignmentsForDetailsCard(result.data.upsertCreditSubmissionAssignmentTitles, creditSubmissionId)
      );
      handleClose();
      showNotification('Changes saved!');
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  const userOptions = getUsersSelectOptions(Object.values(contactsById));

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="span" variant="h6">
          Details
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap="1rem">
          {fields.map((field, index) => (
            <AutocompleteField
              key={field.id}
              control={control}
              name={`contactsAssignments.${index}.contact`}
              options={userOptions}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              textFieldProps={{ label: `Select ${field.title}`, variant: 'outlined', size: 'small' }}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit(handleDetailsDialogSave)} variant="contained" loading={isLoading}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
